export default class StageManager {

  constructor (vue, showBookingTypesFirst = false) {
    this.vue = vue
    this.backBtnContext = 0
    this.currentStageIndex = 0
    this.stages = [
      { id: 0, name: 'Venue', text: 'Select a venue', visible: true, open: true, disabled: false, index: 0 },
      { id: 1, name: 'People & Date', text: 'How many, and when for?', visible: false, open: false, disabled: false, index: 1 },
      { id: 2, name: 'Type', text: 'Select type', visible: false, open: false, disabled: false, index: 2 },
      { id: 3, name: 'Time', text: 'What time?', visible: false, open: false, disabled: false, index: 3 },
      { id: 4, name: 'Confirmation', text: 'Confirm booking', visible: false, open: false, disabled: false, index: 4, fullScreen: true },
      { id: 5, name: 'Other Availability', text: 'Checking other days...', visible: false, open: false, disabled: false, previous: 0, index: 5, fullScreen: true },
      { id: 6, name: 'Error', text: 'Something went wrong...', visible: false, open: false, disabled: false, index: 6, fullScreen: true },
      { id: 7, name: 'Booking Complete', text: 'Booking Complete', visible: false, open: false, disabled: false, index: 7, fullScreen: true },
      { id: 8, name: 'Combo Type', text: 'Select type', visible: false, open: false, disabled: false, index: 8 },
      { id: 9, name: 'Combo Times', text: 'What time?', visible: false, open: false, disabled: false, index: 9 }
    ]

    if (showBookingTypesFirst) {
      this.setIndexesForTypeFirst()
    } else {
      this.setIndexesForDateFirst()
    }
  }

  setIndexesForTypeFirst () {
    // Put Date second
    this.loopStagesAndRunCallback(1, (stageIndex) => {
      this.stages[stageIndex].index = 2
    })

    // And Type first
    this.loopStagesAndRunCallback(2, (stageIndex) => {
      this.stages[stageIndex].index = 1
    })
  }

  setIndexesForDateFirst () {
    // Put Date first
    this.loopStagesAndRunCallback(1, (stageIndex) => {
      this.stages[stageIndex].index = 1
    })

    // Put Type second
    this.loopStagesAndRunCallback(2, (stageIndex) => {
      this.stages[stageIndex].index = 2
    })
  }

  /**
   * Makes the venue selection stage closed, and the People & Date stage both
   * visible and open. Used for when only one venue is requested.
   * @return {Void}
   */
  initJumpAhead () {
    this.openStage(1)
  }

  /**
   * Goes through each stage header and translates it to the required language
   * using the i18n class.
   * @param {i18n} i18n
   * @return {Void}
   */
  translateStages (i18n) {
    for (let i = 0; i < this.stages.length; i++) {
      this.stages[i].text = i18n.get(this.stages[i].text)
    }
  }

  /**
   * Checks if the passed stage ID is valid
   * @param {Number} stageId
   * @return {Boolean}
   */
  stageIdIsValid (stageId) {
    return this.getStageById(stageId) !== null
  }

  /**
   * Returns the details object for the specified stage ID
   * @param {Number} stageId
   * @return {Object|null}
   */
  getStageById (stageId) {
    for (let i = 0; i < this.stages.length; i++) {
      if (this.stages[i].id === stageId) {
        return this.stages[i]
      }
    }
    return null
  }

  stageIsOpen (stageId) {
    return !!this.getStageById(stageId).open
  }

  /**
   * Loops through all the different stages and runs the callback on the stageId
   * passed through
   * @param {Number} stageId
   * @param {Function} callback
   * @return {Function}
   */
  loopStagesAndRunCallback (stageId, callback) {
    for (let i = 0; i < this.stages.length; i++) {
      if (this.stages[i].id === stageId) {
        return callback(i)
      }
    }
  }

  /**
   * Closes all stages except for the one passed through
   * @param {Number} stageId
   * @return {Void}
   */
  closeAllStagesExcept (stageId) {
    for (let i = 0; i < this.stages.length; i++) {
      if (this.stages[i].id !== stageId && this.stages[i].open) {
        this.stages[i].open = false
        this.vue.$emit('stage.closing', i)
      }

      // If the stage is further down, hide it.
      if (this.stages[i].index > this.getStageById(stageId).index && this.stages[i].visible) {
        this.stages[i].visible = false
        this.vue.$emit('stage.hidden', i)
      }
    }
  }

  /**
   * Disables a stage so it cannot be reopened
   * @param {Number} stageId
   * @return {Void}
   */
  disableStage (stageId) {
    this.getStageById(stageId).disabled = true
  }

  /**
   * Ensures that the passed stage id is valid, and then sets the
   * currentStageIndex to the specified stage id
   * @param {Number} stageId
   * @return {Void}
   */
  openStage (stageId) {
    if (!this.stageIdIsValid(stageId)) return
    this.closeAllStagesExcept(stageId)
    this.loopStagesAndRunCallback(stageId, (stageIndex) => {
      this.stages[stageIndex].visible = true
      this.vue.$nextTick(() => {
        this.vue.$emit('stage.visible', stageIndex)
        this.vue.$emit('stage.opening', stageIndex)
        this.stages[stageIndex].open = true
      })
      this.stages[stageIndex].open = true
    })
  }

  /**
   * Returns the current stage of the form
   * @return {Number}
   */
  getCurrentStage () {
    return this.getStageById(this.currentStageIndex)
  }

  /**
   * Sets the text to be displayed at the top of a stage inside the dropdown
   * @param {Number} stageId
   * @param {String} text
   * @return {Boolean}
   */
  setStageText (stageId, text) {
    this.getStageById(stageId).text = text
    return true
  }

  /**
   * Whether or not a stage should be visible
   * @param {Number} stageId
   * @return {Boolean}
   */
  isStageVisible (stageId) {
    return this.getStageById(stageId).visible
  }

  /**
   * Whether or not a stage should be open
   * @param {Number} stageId
   * @return {Boolean}
   */
  isStageOpen (stageId) {
    return this.getStageById(stageId).open
  }

  /**
   * Sets the stageId the user should be taken to when clicking the Back button
   * on the dropdown. ATM, the back button is only present on the 'Checking
   * Availability' stage.
   * @param {Number} stageId
   * @return {Boolean}
   */
  setBackButtonContext (stageId) {
    this.backBtnContext = stageId
    return true
  }

  /**
   * Gets the stageId of where we should go back to
   * @return {Number}
   */
  getBackButtonContext () {
    return this.backBtnContext
  }
}



// WEBPACK FOOTER //
// ./src/managers/StageManager.js